import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Column } from 'primereact/column';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { addMonths, differenceInDays } from 'date-fns';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Container } from './styles';

import ClienteService from '../../services/ClienteService';
import AuthService from '../../services/AuthService';
import history from '../../services/history';
import {
  getPageParams,
  errorHandle,
  calcNaxItemsPage,
  isScreenMobile,
  formatDate,
  newDateOnly,
} from '../../util/functions';

import { showMessage } from '../../components/MessageDialog';

import BotaoMenuGrid from '../../components/BotaoMenuGrid';

import ClienteCrud from './crud';
import { StateScreen } from '../constants';
import CrudHeader from '../../components/CrudHeader';
import DataTableSp from '../../components/DataTableSp';
import LabelSp from '../../components/LabelSp';
import InputMaskSp from '../../components/InputMaskSp';
import RevendaService from '../../services/RevendaService';
import DropdownSp from '../../components/DropdownSp';
import { cfgPtBr, listaUfs } from '../../config/Constantes';
import CalendarSp from '../../components/CalendarSp';
import DropDownLazy from '../../components/DropdownLazy';
import MunicipioService from '../../services/MunicipioService';
import ContratoOrcamento from '../../components/ContratoOrcamento/contrato';

export default function Cliente(props) {
  // useSelectors
  const filterVisible = useSelector(state => state.global.filterVisible);

  // useMemo
  const pageParams = useMemo(() => getPageParams(props.match.params, props.location.search), [
    props.location.search,
    props.match.params,
  ]);

  const filterService = useMemo(() => {
    const f = ClienteService.getFilter();
    f.idRevenda = AuthService.getUsuario().idRevenda;
    return f;
  }, []);

  // useStates
  const toBack = pageParams.toBack || '/clientes';

  const [filter, setFilter] = useState(filterService);
  const [clientes, setClientees] = useState([]);

  const [pageLimit, setPageLimit] = useState(filterService.limit);
  const [first, setFirst] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSelected, setPageSelected] = useState(0);
  const [revendas, setRevendas] = useState();
  const [funcTimeOut, setFuncTimeOut] = useState();
  const [tipoPessoaSelecionada, setTipoPessoaSelecionada] = useState('J');
  const [municipio, setMunicipio] = useState(null);
  const [ufSelecionada, setUfSelecionada] = useState('MG');
  const [clienteSelecionado, setClienteSelecionado] = useState(undefined);

  // useCallbacks

  const calcLimit = useCallback(() => {
    let ret;
    if (filterVisible) {
      ret = calcNaxItemsPage(8, 12, 17);
    } else {
      ret = calcNaxItemsPage(13, 17, 22);
    }
    setPageLimit(ret);
    return ret;
  }, [filterVisible]);

  const carregaClienteOrcamento = async id => {
    const retorno = await ClienteService.findById(id);
    setClienteSelecionado(retorno);
  };

  const loadRevenda = useCallback(async () => {
    const r = await RevendaService.findAll({
      limit: 999,
    });

    const retorno = r.items.map(e => {
      return {
        label: e.nome,
        value: e.id,
      };
    });

    setRevendas(retorno);
  }, []);

  const handleBuscar = useCallback(
    async (_filter, _page, resetPage = true) => {
      _filter.page = _page || 0;
      _filter.limit = calcLimit();

      try {
        const result = await ClienteService.findAll({ ..._filter, resumo: 'resumo' });
        setClientees(result.items);

        setTotalRecords(result.totalRecords);
        if (resetPage) {
          setFirst(0);
        }
      } catch (err) {
        errorHandle(err);
      }
    },
    [calcLimit]
  );

  const loadMunicipio = useCallback(async (_nome, uf) => {
    if (_nome !== undefined) {
      const r = await MunicipioService.findAll({ nome: _nome, uf, limit: 50 });

      const retorno = r.items.map(e => {
        return {
          label: e.nome,
          value: e.id,
        };
      });
      return retorno;
    }
    return [];
  }, []);

  const setFilterAndSearch = useCallback(
    async _filterValue => {
      if (JSON.stringify(_filterValue) !== JSON.stringify(filter)) {
        setFilter(_filterValue);

        if (funcTimeOut) {
          clearTimeout(funcTimeOut);
        }
        const func = setTimeout(async () => {
          handleBuscar(_filterValue);
        }, 800);
        setFuncTimeOut(func);
      }
    },
    [filter, funcTimeOut, handleBuscar]
  );

  function onPage(event) {
    const pagina = event.first / event.rows;
    setPageSelected(pagina);
    setFirst(event.first);
    handleBuscar(filter, pagina, false);
  }

  // useCallbacks
  const excluirRegistro = useCallback(
    async _id => {
      try {
        await ClienteService.delete(_id);
        toast.success('Registro excluído com sucesso.');
        handleBuscar(filter);
      } catch (err) {
        errorHandle(err);
      }
    },
    [filter, handleBuscar]
  );

  // functions
  function getTitle() {
    const titleDefault = 'Cliente';
    let titleAdd = '';

    if (pageParams.stateScreen === StateScreen.stSearch) {
      titleAdd = '';
    }
    if (pageParams.stateScreen === StateScreen.stInsert) {
      titleAdd = '(Novo)';
    }
    if (pageParams.stateScreen === StateScreen.stUpdate) {
      titleAdd = `(Alterando Id: ${pageParams.idSelected})`;
    }
    if (pageParams.stateScreen === StateScreen.stView) {
      titleAdd = ` (Visualizando Id: ${pageParams.idSelected})`;
    }

    if (!isScreenMobile()) {
      return `${titleDefault} ${titleAdd}`;
    }

    return titleDefault;
  }

  const confirmaExclusao = useCallback(
    id => {
      showMessage('Confirmação', 'Confirma a exclusão do registro?', idx => {
        if (idx === 1) {
          excluirRegistro(id);
        }
      });
    },
    [excluirRegistro]
  );

  function bloqueiaRevenda() {
    return (
      AuthService.getUsuario() &&
      AuthService.getUsuario().idRevenda &&
      !AuthService.isPerfilGerente()
    );
  }

  // useEffects
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      handleBuscar(filter, 0, true);
    }
    loadRevenda();
    // desativado para evitar que a cada vez que o cliente digitasse o sistema buscasse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffects
  // Responsavel por recalcular a quantidade de registros da grid quando a div do filtro ocultar
  // ou ficar visivel
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      calcLimit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcLimit]);
  const footer = (
    <div>
      <Button
        label="Fechar"
        icon="pi pi-times"
        onClick={() => {
          setClienteSelecionado(undefined);
        }}
        className="p-button-danger"
      />
    </div>
  );
  const doAlterar = async registro => {
    if (!(await verificaPermissaoEhAltera(registro))) {
      return;
    }
    history.push(`/clientes/${registro.id}`);
  };
  const verificaPermissaoEhAltera = async registro => {
    if (AuthService.isPerfilGerente()) {
      return true;
    }
    const clienteAlteracao = await ClienteService.findById(registro.id);
    if (!clienteAlteracao) {
      toast.error('Cliente não encontrado');
      return false;
    }
    if (clienteAlteracao && clienteAlteracao.chave && clienteAlteracao.id) {
      toast.error('Já existe chave para o cliente. Você não pode alterar');
      return false;
    }

    const dataAtual = newDateOnly();
    const dataInclusao = new Date(clienteAlteracao.dataInclusao) || new Date();
    const dias = differenceInDays(dataAtual, dataInclusao);

    if (dias > 1) {
      toast.error('Você não tem permissão para alteração com mais de 1 dia do lançamento');
      return false;
    }

    return true;
  };

  // renders
  return (
    <Container className="container-page page-enter-tab">
      {clienteSelecionado && (
        <Dialog
          closable={false}
          footer={footer}
          header="Valores do Contrato"
          visible={!!clienteSelecionado}
          style={{ minWidth: '350px', maxWidth: '800px', width: '95%' }}
          contentStyle={{ minHeight: 550 }}
          modal
          onHide={() => {
            setClienteSelecionado(undefined);
          }}
        >
          <div className="p-grid">
            <ContratoOrcamento
              contrato={{ ...clienteSelecionado }}
              dispositivosUsados={0}
              permissaoAdminAlteracao={() => true}
              orcamento
              permissaoModulos={() => true}
              viewMode={() => false}
              setContrato={setClienteSelecionado}
            />
          </div>
        </Dialog>
      )}
      <div className="p-grid">
        <CrudHeader
          title={getTitle()}
          disabledButtonInsert={!AuthService.checkRoles('CLIENTE_INSERIR')}
          showButtonFilter={pageParams.stateScreen === StateScreen.stSearch}
          showButtonInsert
          showButtonSearch
          titleFilterDefault="Buscar por Nome"
          handleFilterDefault={text => {
            filter.nome = text;
            setFilter({ ...filter, nome: text });
            handleBuscar(filter);
          }}
          handleClearFilters={() => {
            setFilter({
              ...filter,
              nome: '',
              numeroContrato: '',
              idRevenda: null,
              cpfCnpj: '',
              tipoInativo: 0,
              idMunicipio: null,

              dataInicio: addMonths(new Date(), -12 * 10),
              dataFim: new Date(),
            });
            setMunicipio(null);
          }}
          handleButtonInsert={() => history.push('/clientes/insert')}
          handleButtonSearch={() => handleBuscar(filter)}
        >
          <div
            className="p-grid p-col-7 p-sm-5 p-lg-5 p-fluid"
            style={{ margin: 0, padding: 0 }}
          >
            <div className="p-col-6 p-fluid">
              <LabelSp htmlFor="dataInicial">De: </LabelSp>
              <CalendarSp
                showIcon
                autoFocus
                showOnFocus={false}
                appendTo={document.body}
                dateFormat="dd/mm/yy"
                yearNavigator
                locale={cfgPtBr}
                id="dataInicial"
                value={filter.dataInicio}
                yearRange="2010:2040"
                onChange={e => {
                  setFilterAndSearch({ ...filter, dataInicio: e.value });
                }}
              />
            </div>
            <div className="p-col-6 p-fluid">
              <LabelSp htmlFor="dataFinal">Até: </LabelSp>
              <CalendarSp
                appendTo={document.body}
                showIcon
                showOnFocus={false}
                locale={cfgPtBr}
                id="dataFinal"
                dateFormat="dd/mm/yy"
                value={filter.dataFim}
                yearNavigator
                yearRange="2010:2040"
                onChange={e => setFilterAndSearch({ ...filter, dataFim: e.value })}
              />
            </div>
          </div>

          <div className="p-col-5 p-sm-2 p-lg-2 p-fluid">
            <LabelSp>Contrato</LabelSp>
            <InputMaskSp
              mask="999999"
              value={filter.numeroContrato}
              onChange={e => {
                setFilterAndSearch({ ...filter, numeroContrato: e?.value });
              }}
            />
          </div>
          <div className="p-col-5 p-sm-2 p-lg-2 p-fluid">
            <LabelSp>Tp. Pessoa</LabelSp>
            <DropdownSp
              value={tipoPessoaSelecionada}
              options={[
                { value: 'J', label: 'Jurídica' },
                { value: 'F', label: 'Física' },
              ]}
              onChange={e => {
                setTipoPessoaSelecionada(e?.value);
              }}
            />
          </div>
          {tipoPessoaSelecionada === 'F' ? (
            <div className="p-col-7 p-sm-3 p-lg-3 p-fluid">
              <LabelSp>CPF</LabelSp>
              <InputMaskSp
                mask="999.999.999-99"
                value={filter.cpfCnpj}
                onChange={e => {
                  setFilterAndSearch({ ...filter, cpfCnpj: e?.value });
                }}
              />
            </div>
          ) : (
            <div className="p-col-7 p-sm-3 p-lg-3 p-fluid">
              <LabelSp>CNPJ</LabelSp>
              <InputMaskSp
                mask="99.999.999/9999-99"
                value={filter.cpfCnpj}
                onChange={e => {
                  setFilterAndSearch({ ...filter, cpfCnpj: e?.value });
                }}
              />
            </div>
          )}

          <div
            className="p-grid p-col-12 p-sm-5 p-lg-5 p-fluid"
            style={{ margin: 0, padding: 0 }}
          >
            <div className="p-col-3 p-fluid">
              <LabelSp>UF</LabelSp>
              <DropdownSp
                placeholder="Selecione"
                value={ufSelecionada}
                options={listaUfs}
                filter
                disabled
                onChange={e => setUfSelecionada(e.target.value)}
              />
            </div>

            <div className="p-col-9 p-fluid">
              <LabelSp>Município</LabelSp>
              <DropDownLazy
                autoLoad
                showClear
                placeholder="Todos"
                onChange={e => {
                  setFilterAndSearch({ ...filter, idMunicipio: e?.value });
                  setMunicipio(e);
                }}
                value={municipio}
                onFilter={async txtFilter => {
                  const retorno = await loadMunicipio(txtFilter, ufSelecionada);
                  return retorno;
                }}
              />
            </div>
          </div>
          <div className="p-col-8 p-sm-4 p-lg-4 p-fluid">
            <LabelSp>Revenda</LabelSp>
            <DropdownSp
              placeholder="Todos"
              showClear
              disabled={bloqueiaRevenda()}
              onChange={e => {
                setFilterAndSearch({ ...filter, idRevenda: e?.value });
              }}
              options={revendas}
              value={filter.idRevenda}
            />
          </div>

          <div className="p-col-4 p-sm-3 p-lg-3 p-fluid">
            <LabelSp>Ativo</LabelSp>
            <DropdownSp
              value={filter.tipoInativo}
              options={[
                { value: 0, label: 'Todos' },
                { value: 1, label: 'Ativos' },
                { value: 2, label: 'Inativos' },
              ]}
              onChange={e => {
                setFilterAndSearch({ ...filter, tipoInativo: e?.value });
              }}
            />
          </div>
        </CrudHeader>
        {pageParams.stateScreen === StateScreen.stSearch ? renderSearch() : renderCrud()}
      </div>
    </Container>
  );

  function renderSearch() {
    return (
      <>
        <div className="p-col-12 p-fluid datatable-doc-demo">
          <DataTableSp
            value={clientes}
            style={{ marginBottom: '2px' }}
            paginator
            rows={pageLimit}
            lazy
            responsive
            totalRecords={totalRecords}
            first={first}
            onPage={onPage}
          >
            <Column
              field="numeroContrato"
              header="Contrato"
              className="grid-col-id"
              style={{ width: 65 }}
            />
            <Column
              field="nome"
              className="grid-col grid-col-nowrap"
              header="Nome"
              body={rowData => <span title={rowData.nome}>{rowData.nome}</span>}
            />
            <Column
              field="nomeFantasia"
              className="grid-col grid-col-nowrap"
              header="Fantasia"
              body={rowData => (
                <span title={rowData.nomeFantasia}>{rowData.nomeFantasia}</span>
              )}
            />
            <Column
              field="cpfCnpj"
              className="grid-col p-p-5"
              style={{ width: 133 }}
              header="CPF/CNPJ"
            />
            <Column
              field="fone"
              className="grid-col"
              style={{ width: 103 }}
              header="Telefone"
            />
            <Column
              field="inativo"
              className="grid-col grid-col-center"
              style={{ width: 55 }}
              header="Inativo"
              body={rowData => {
                return rowData.inativo ? (
                  <span style={{ color: 'red' }}>Sim</span>
                ) : (
                  <span style={{ color: 'blue' }}>Não</span>
                );
              }}
            />

            <Column
              field="dataInicioContrato"
              className="grid-col grid-col-data p-p-8"
              header="Inic. Contr."
              body={rowData => formatDate(rowData.dataInicioContrato, 'dd/MM/yyyy')}
            />
            <Column
              field="dataFimContrato"
              className="grid-col grid-col-data p-p-8"
              header="Fim Contr."
              body={rowData => formatDate(rowData.dataFimContrato, 'dd/MM/yyyy')}
            />

            <Column
              className="gid-col-acoes-35"
              bodyStyle={{ textAlign: 'end' }}
              body={renderButtonOp}
            />
          </DataTableSp>
        </div>
      </>
    );
  }

  function renderButtonOp(rowData) {
    return (
      <BotaoMenuGrid
        labels={['Visualizar', 'Alterar', 'Excluir', 'Orçamento']}
        icons={['pi pi-search', 'pi pi-pencil', 'pi pi-trash', 'pi pi-wallet']}
        handles={[
          () => history.push(`/clientes/${rowData.id}?view`),
          () => {
            doAlterar(rowData);
          },
          () => confirmaExclusao(rowData.id),
          () => carregaClienteOrcamento(rowData.id),
        ]}
        disableds={[
          false,
          !AuthService.checkRoles('CLIENTE_ALTERAR'),
          !AuthService.checkRoles('CLIENTE_EXCLUIR'),
          false,
        ]}
      />
    );
  }

  function renderCrud() {
    return (
      <ClienteCrud
        idSelected={pageParams.idSelected}
        stateScreen={pageParams.stateScreen}
        onClose={() => {
          history.push(toBack);
          handleBuscar(filter, pageSelected, false);
        }}
      />
    );
  }
}
